import React, { type ReactNode, memo } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { EMPTY_VALUE_ID } from '../../../constants.tsx';
import type { ExtendedOption, Group, IssuesByGroupIdentity } from '../../../types/common.tsx';
import type { CustomComponents } from '../../../types/custom-components.tsx';
import type {
	CollapsedSwimlanes,
	ColumnsHighlightColors,
	IsCardDropDisabledForColumn,
	OnSwimlaneToggle,
} from '../../../types/ui.tsx';
import { createGroupIdentityPair } from '../../../utils/board.tsx';
import { Cell } from '../cell/main.tsx';
import {
	createGroupNullObject,
	useClearGroupNullObjectCache,
} from '../../../utils/group-null-object.tsx';
import { DraggableGroupStickyHeader } from './draggable-group-sticky-header/index.tsx';

type BodyRowProps = {
	collapsedSwimlanes: CollapsedSwimlanes;
	columnsHighlightColors: ColumnsHighlightColors;
	components: CustomComponents;
	draggableId: string;
	extendedOptions: ExtendedOption<unknown>[];
	groupIdentity?: string;
	group: Group;
	hideEmptyColumns: boolean;
	hideEmptyGroups: boolean;
	idsByColumn: IssuesByGroupIdentity;
	index: number;
	isFooterVisible: boolean;
	isLastGroup: boolean;
	isReadOnly: boolean;
	verticalFieldValue: unknown;
	columnsFieldKey: string;
	groupsFieldKey: string;
	isCardDropDisabledForColumn: IsCardDropDisabledForColumn;
	onSwimlaneToggle: OnSwimlaneToggle;
};

const BodyRow = memo((props: BodyRowProps) => {
	const {
		collapsedSwimlanes,
		columnsHighlightColors,
		components,
		draggableId,
		extendedOptions,
		groupIdentity,
		group,
		hideEmptyColumns,
		hideEmptyGroups,
		idsByColumn,
		index,
		isFooterVisible,
		isLastGroup,
		isReadOnly,
		verticalFieldValue,
		columnsFieldKey,
		groupsFieldKey,
		isCardDropDisabledForColumn,
		onSwimlaneToggle,
	} = props;
	const columnsCount = hideEmptyColumns ? group.columns.length : extendedOptions.length;

	const cardsCountInGroup = group.columns.reduce((sum, col) => sum + col.cards.length, 0);
	const isExpandedGroup = collapsedSwimlanes[String(groupIdentity)] === undefined;

	const isVisible = isExpandedGroup
		? cardsCountInGroup > 0
		: collapsedSwimlanes[String(groupIdentity)] === false;

	const isEmptyGroup = cardsCountInGroup === 0;
	const isHiddenGroup = hideEmptyGroups && isEmptyGroup;

	return (
		<Box xcss={[rowContainerStyles, isHiddenGroup && rowContainerHiddenStyles]}>
			<DraggableGroupStickyHeader
				columnsCount={columnsCount}
				components={components}
				draggableId={`row-${draggableId}`}
				groupIdentity={groupIdentity}
				index={index}
				isLastGroup={isLastGroup}
				isReadOnly={isReadOnly}
				isVisible={isVisible}
				localIssuesCount={cardsCountInGroup}
				onSwimlaneToggle={onSwimlaneToggle}
			/>
			{isVisible ? (
				<Box xcss={rowContentContainerStyles}>
					{extendedOptions.map((option, columnIndex) => {
						const columnInRow = group.columns.find(
							(column) => column.columnUid === option.groupIdentity || EMPTY_VALUE_ID,
						);
						const ideaIds = columnInRow?.cards.map((card) => card.uid) ?? [];
						const idsInColumn =
							(option.groupIdentity === undefined
								? idsByColumn.empty
								: idsByColumn.groups[option.groupIdentity]) ?? [];

						return (
							<Cell
								key={`${columnIndex}-${option.groupIdentity || ''}`}
								columnsHighlightColors={columnsHighlightColors}
								components={components}
								droppableId={createGroupIdentityPair(draggableId, option.droppableId)}
								fieldValue={option.value}
								groupIdentity={option.groupIdentity}
								hideEmptyColumns={hideEmptyColumns}
								ideaIds={ideaIds}
								index={columnIndex}
								isCardDropDisabled={isReadOnly || isCardDropDisabledForColumn(option.groupIdentity)}
								isEmptyColumn={idsInColumn.length === 0}
								isFooterVisible={isFooterVisible}
								isReadOnly={isReadOnly}
								verticalFieldValue={verticalFieldValue}
								verticalGroupIdentity={groupIdentity}
								columnsFieldKey={columnsFieldKey}
								groupsFieldKey={groupsFieldKey}
								{...(fg('jpd_group_by_and_columns_config_ufo_fixes') ? { isEmptyGroup } : {})}
							/>
						);
					})}
				</Box>
			) : null}
		</Box>
	);
});

BodyRow.displayName = 'SwimlanesBodyRow';

export type SwimlanesBodyProps = {
	children: ReactNode;
	collapsedSwimlanes: Record<string, boolean>;
	columnsHighlightColors: ColumnsHighlightColors;
	components: CustomComponents;
	extendedOptions: ExtendedOption<unknown>[];
	extendedVerticalOptions: ExtendedOption<unknown>[];
	groups: Group[];
	hideEmptyGroups: boolean;
	hideEmptyColumns: boolean;
	idsByColumn: IssuesByGroupIdentity;
	isFooterVisible: boolean;
	isReadOnly: boolean;
	columnsFieldKey: string;
	groupsFieldKey: string;
	isCardDropDisabledForColumn: IsCardDropDisabledForColumn;
	onSwimlaneToggle: OnSwimlaneToggle;
};

export const SwimlanesBody = memo(
	({
		children,
		collapsedSwimlanes,
		columnsHighlightColors,
		components,
		extendedOptions,
		extendedVerticalOptions,
		groups,
		hideEmptyGroups,
		hideEmptyColumns,
		idsByColumn,
		isFooterVisible,
		isReadOnly,
		columnsFieldKey,
		groupsFieldKey,
		isCardDropDisabledForColumn,
		onSwimlaneToggle,
	}: SwimlanesBodyProps) => {
		useClearGroupNullObjectCache();

		return (
			<Box xcss={rowsContainerStyles}>
				{children}

				{extendedVerticalOptions.map((option, index) => {
					let group = groups.find((g) => g.uid === (option.groupIdentity || EMPTY_VALUE_ID));

					if (!group) {
						// If we couldn't find a group it means that `hideEmptyGroups` should be `true`.
						// To have backward compatibility and 1:1 implementation as the current board view,
						// we create here a stable `group object` reference thus rendering an "empty" `BodyRow`.
						// Ideally, we should be able to just `return null` in a such case.
						// Therefore for a short period of time we will number of `fireErrorAnalytics` calls
						// to ensure that there is no edge case where `groups` could be out of sync
						// with `extendedVerticalOptions` & `hideEmptyGroups`.
						group = createGroupNullObject(option.groupIdentity || EMPTY_VALUE_ID);

						if (!hideEmptyGroups) {
							fireErrorAnalytics({
								meta: {
									id: 'swimlanesBodyNotFoundGroup',
									packageName: 'jiraPolarisLibBoard',
									teamName: 'jpd-horizon',
								},
								attributes: {
									groupIdentity: option.groupIdentity ? 'OPTION_GROUP_IDENTITY' : 'EMPTY_VALUE_ID',
									extendedVerticalOptionsLength: extendedVerticalOptions.length,
									groupsCount: groups.length,
									hideEmptyGroups,
								},
								sendToPrivacyUnsafeSplunk: true,
							});
						}
					}

					return (
						<BodyRow
							key={option.droppableId}
							collapsedSwimlanes={collapsedSwimlanes}
							columnsHighlightColors={columnsHighlightColors}
							components={components}
							draggableId={option.droppableId}
							extendedOptions={extendedOptions}
							group={group}
							groupIdentity={option.groupIdentity}
							hideEmptyGroups={hideEmptyGroups}
							hideEmptyColumns={hideEmptyColumns}
							index={index}
							isFooterVisible={isFooterVisible}
							isLastGroup={index === extendedVerticalOptions.length - 1}
							isReadOnly={isReadOnly}
							verticalFieldValue={option.value}
							idsByColumn={idsByColumn}
							isCardDropDisabledForColumn={isCardDropDisabledForColumn}
							onSwimlaneToggle={onSwimlaneToggle}
							columnsFieldKey={columnsFieldKey}
							groupsFieldKey={groupsFieldKey}
						/>
					);
				})}
			</Box>
		);
	},
);

SwimlanesBody.displayName = 'SwimlanesBody';

const rowContainerStyles = xcss({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
});

const rowContainerHiddenStyles = xcss({
	display: 'none',
});

const rowContentContainerStyles = xcss({
	width: '100%',
	display: 'flex',
});

const rowsContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	marginBottom: 'space.200',
});
